/**
 * Ukrainian formatting helpers — plurals, prices, dates, addresses.
 * Kept dependency-free so it works with the project's older build toolchain.
 */

import { EXCHANGE_RATES } from "../siteConfig";

/**
 * Ukrainian plural selection.
 * plural(3, "кімната", "кімнати", "кімнат") -> "кімнати"
 */
export function plural(count, one, few, many) {
  var n = Math.abs(parseInt(count, 10)) || 0;
  var n10 = n % 10;
  var n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

export function roomsLabel(rooms) {
  var n = parseInt(rooms, 10);
  if (!n) return "";
  return n + " " + plural(n, "кімната", "кімнати", "кімнат");
}

export function roomsShort(rooms) {
  var n = parseInt(rooms, 10);
  if (!n) return "";
  return n + "-кімнатна";
}

export function floorLabel(floor) {
  var n = parseInt(floor, 10);
  if (!n && n !== 0) return "";
  return n + " поверх";
}

/**
 * Поле «валюта» в базі — це вільний текст, який рієлтор вводить руками. Крім
 * "$", "грн" і "€" там трапляється "$ по курсу НБУ", "$ ПО НБУ",
 * "Євро по курсу ПРИВАТ24" тощо.
 *
 * Тому валюту визначаємо за ВМІСТОМ рядка, а не точним збігом. Раніше
 * незнайомий рядок мовчки рахувався гривнею — і оголошення за
 * "450 $ по курсу НБУ" ставало найдешевшим у всій базі (450 замість 19 800).
 */
var CURRENCY_PATTERNS = [
  { code: "USD", test: /\$|USD|ДОЛ/ },
  { code: "EUR", test: /€|EUR|ЄВРО|ЕВРО/ },
  { code: "UAH", test: /₴|UAH|ГРН|ГРИВ/ },
];

var CURRENCY_SYMBOL = { UAH: "грн", USD: "$", EUR: "€" };

/** Вільний текст -> код валюти. Не впізнали — вважаємо гривнею. */
export function currencyCode(currency) {
  if (!currency) return "UAH";
  var raw = String(currency).toUpperCase();
  for (var i = 0; i < CURRENCY_PATTERNS.length; i++) {
    if (CURRENCY_PATTERNS[i].test.test(raw)) return CURRENCY_PATTERNS[i].code;
  }
  return "UAH";
}

export function currencyLabel(currency) {
  return CURRENCY_SYMBOL[currencyCode(currency)];
}

/**
 * Те, що рієлтор дописав біля валюти ("по курсу НБУ") — це умова оплати.
 * Вона корисна орендарю, але не має потрапляти в сам рядок ціни.
 */
export function currencyNote(currency) {
  if (!currency) return "";
  var rest = String(currency)
    .replace(/\$|€|₴/g, " ")
    .replace(/USD|EUR|UAH/gi, " ")
    .replace(/ГРИВЕНЬ|ГРИВНЯ|ГРН\.?|ЄВРО|ЕВРО|ДОЛАРІВ|ДОЛАРА|ДОЛАР|ДОЛ\.?/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return rest.length > 2 ? rest : "";
}

/** 12500 -> "12 500" (non-breaking thin spaces so prices never wrap) */
export function formatNumber(value) {
  var n = Number(value);
  if (!isFinite(n)) return "";
  var s = Math.round(n).toString();
  var out = "";
  var c = 0;
  for (var i = s.length - 1; i >= 0; i--) {
    out = s[i] + out;
    c++;
    if (c % 3 === 0 && i > 0) out = " " + out;
  }
  return out;
}

export function formatPrice(cost, currency) {
  if (cost === undefined || cost === null || cost === "") return "Ціна за запитом";
  return formatNumber(cost) + " " + currencyLabel(currency) + "/міс";
}

export function formatPriceShort(cost, currency) {
  if (cost === undefined || cost === null || cost === "") return "—";
  return formatNumber(cost) + " " + currencyLabel(currency);
}

/**
 * Ціни в базі змішані — частина в гривні, частина в доларі. Для будь-якого
 * порівняння (фільтр, сортування, «найдешевша пропозиція») суми треба звести
 * до однієї валюти; показуємо ж завжди у валюті самого оголошення.
 * Курси лежать у siteConfig.EXCHANGE_RATES.
 */
export function toBaseCurrency(cost, currency) {
  var amount = Number(cost);
  if (!isFinite(amount) || amount <= 0) return null;

  var rate = EXCHANGE_RATES[currencyCode(currency)];
  return amount * (rate || 1);
}

/** Те саме для об'єкта оголошення. */
export function itemPriceInBase(item) {
  return item ? toBaseCurrency(item.cost, item.currency) : null;
}

/**
 * A price-range bound as a number, or null for "no bound".
 *
 * The two sides of the app spell "empty" differently — the public filters use
 * "", the realtor's objects header uses -1 and its archives header uses "" —
 * so every one of those has to mean the same thing here.
 */
export function priceBound(value) {
  if (value === null || value === undefined || value === "") return null;
  var amount = Number(value);
  if (!isFinite(amount) || amount < 0) return null;
  return amount;
}

/**
 * Does this listing fall inside a price range given in hryvnia?
 *
 * The realtor panel compared `cost` directly, which reads a «450 $» listing as
 * 450 ₴ — so a search for 6000–20000 hid every dollar listing in the database
 * and showed every one under 20000 dollars. This is the same comparison the
 * public catalogue runs, down to the treatment of listings with no usable
 * price: they stay out of an explicit price search rather than counting as free.
 */
export function priceInRange(item, fromCost, toCost) {
  var from = priceBound(fromCost);
  var to = priceBound(toCost);
  if (from === null && to === null) return true;

  var price = itemPriceInBase(item);
  if (price === null) return false;
  if (from !== null && price < from) return false;
  if (to !== null && price > to) return false;
  return true;
}

/**
 * The highest rate any currency converts at.
 *
 * DynamoDB cannot multiply, so a server-side `cost` filter cannot know a
 * listing's currency. Dividing the lower bound by this gives a range wide
 * enough that no candidate is filtered out before `priceInRange` gets to judge
 * it properly on the client.
 */
export function maxExchangeRate() {
  var rates = EXCHANGE_RATES;
  var max = 1;
  for (var key in rates) {
    if (Object.prototype.hasOwnProperty.call(rates, key) && rates[key] > max) {
      max = rates[key];
    }
  }
  return max;
}

var MONTHS_GEN = [
  "січня", "лютого", "березня", "квітня", "травня", "червня",
  "липня", "серпня", "вересня", "жовтня", "листопада", "грудня",
];

export function formatDate(ms) {
  var n = Number(ms);
  if (!n) return "";
  var d = new Date(n);
  if (isNaN(d.getTime())) return "";
  return d.getDate() + " " + MONTHS_GEN[d.getMonth()] + " " + d.getFullYear();
}

/** "щойно" / "3 дні тому" / a plain date for older entries */
export function relativeDate(ms) {
  var n = Number(ms);
  if (!n) return "";
  var days = Math.floor((Date.now() - n) / 86400000);
  if (days < 0) return formatDate(n);
  if (days === 0) return "сьогодні";
  if (days === 1) return "вчора";
  if (days < 7) return days + " " + plural(days, "день", "дні", "днів") + " тому";
  if (days < 30) {
    var w = Math.floor(days / 7);
    return w + " " + plural(w, "тиждень", "тижні", "тижнів") + " тому";
  }
  return formatDate(n);
}

/** ISO date string for the "Europe/Kyiv" civil day, e.g. "2026-09-08". */
export function kyivDateKey(dateObj) {
  var d = dateObj || new Date();
  // sv-SE gives an ISO-shaped date; the timeZone option does the real work.
  try {
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Europe/Kyiv",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch (e) {
    var pad = function (v) {
      return v < 10 ? "0" + v : "" + v;
    };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
  }
}

/** Human title for a listing: "2-кімнатна квартира, вул. Городоцька" */
export function listingTitle(item) {
  if (!item) return "Квартира";
  var parts = [];
  if (item.rooms) parts.push(roomsShort(item.rooms) + " квартира");
  else parts.push("Квартира");
  if (item.street) parts.push("вул. " + cleanStreet(item.street));
  return parts.join(", ");
}

/** The admin panel sometimes appends internal markers to the street name. */
export function cleanStreet(street) {
  if (!street) return "";
  return String(street).replace(/продз/gi, "").trim();
}

export function districtLabel(region) {
  if (!region) return "";
  var r = String(region).trim();
  if (/район/i.test(r)) return r;
  return r + " район";
}

export function mapsUrl(item) {
  if (!item) return "https://www.google.com/maps";
  var q = [
    "Львів",
    item.region ? districtLabel(item.region) : "",
    item.street ? "вулиця " + cleanStreet(item.street) : "",
  ]
    .filter(Boolean)
    .join(", ");
  return "https://www.google.com/maps/search/" + encodeURIComponent(q);
}

/**
 * The admin "info" field is free text that may contain a bare URL or a
 * [https://...] marker. Split it into text + links so we can render safely
 * without dangerouslySetInnerHTML.
 */
export function parseInfo(info) {
  var text = info ? String(info) : "";
  var links = [];
  text = text.replace(/\[([^\]]+)\]/g, function (match, inner) {
    if (/^https?:\/\//i.test(inner.trim())) {
      links.push(inner.trim());
      return " ";
    }
    return match;
  });
  text = text.replace(/https?:\/\/\S+/gi, function (url) {
    links.push(url);
    return " ";
  });
  return { text: text.replace(/\s{2,}/g, " ").trim(), links: links };
}

export function truncate(text, max) {
  var t = text ? String(text) : "";
  if (t.length <= max) return t;
  return t.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}
